import { Callout, Card, Input, Selector } from '@casumo/dojo-react';
import { ChangeEvent, FC, KeyboardEvent, memo, ReactNode, useMemo } from 'react';
import ConsentField from '../components/consent.tsx';
import CountrySelector from '../components/countrySelector.tsx';
import DepositIncrementer from '../components/depositIncrementer.tsx';
import DepositLimitsWrapper from '../components/depositLimits/depositLimitsWrapper.tsx';
import DepositTypeSelector from '../components/depositTypeSelector.tsx';
import { EnterAddressManuallyText } from '../components/enterAddressManuallyText.tsx';
import IdentificationNumberES from '../components/identificationNumberES.tsx';
import AutoCompleteAddressInput from '../components/inputs/autoCompleteAddressInput.tsx';
import DateInput from '../components/inputs/dateInput.tsx';
import EmailInput from '../components/inputs/emailInput.tsx';
import EsPasswordInput from '../components/inputs/esPasswordInput.tsx';
import IdentityProtectedPassword from '../components/inputs/identityProtectedPassword.tsx';
import PasswordInput from '../components/inputs/passwordInput.tsx';
import { PhoneNumberInput } from '../components/inputs/phoneNumberInput.tsx';
import PostcodeInput from '../components/inputs/postcodeInput.tsx';
import { MultiMarketingConsent } from '../components/multiMarketingConsent/MultiMarketingConsent.tsx';
import ProvinceSelector from '../components/provinceSelector.tsx';
import WelcomeOffer from '../components/welcomeOffers/welcomeOfferSelection.tsx';
import WoCallout from '../components/woCallout.tsx';
import SelectorDrawer from '../design-system/selectorDrawer.tsx';
import Text from '../design-system/text.tsx';
import useEnterKeyNavigation from '../hooks/useEnterKeyNavigation.tsx';
import useFieldRenderer from '../hooks/useFieldRenderer.tsx';
import useFieldValidator from '../hooks/useFieldValidator.tsx';
import { useFlowContext } from '../hooks/useFlowContext.tsx';
import { useRegistrationStore } from '../store/store.ts';
import { FieldType, TCardField, TDependsOn, TField, TInputField, TStep } from '../types/types.ts';
import { ENVIRONMENTS, NAME_FIELD_MAX_LENGTH, NAME_FIELDS, TEST_ID } from '../utils/constants.ts';
import { REACT_APP_ENVIRONMENT } from '../utils/envConstants.ts';
import { GTM_FOCUS_FIELDS } from '../utils/gtm/constants.ts';
import { interpolate } from '../utils/utils.ts';

type RegistryProps = {
  field: TField;
  totalSteps?: number;
  error?: boolean;
  errorMessage?: string;
  value: string;
  step: TStep;
};

type StrippedProps<T> = Omit<T, 'fieldType'>;

function stripFieldProps<T extends {
  fieldType?: FieldType;
  gtmPayload?: Record<string, string>;
  flex?: boolean;
  dependsOn?: TDependsOn[];
}>(props: T): StrippedProps<T> {
  const { fieldType: _fieldType, gtmPayload: _gtmPayload, flex: _flex, dependsOn: _dependsOn, ...rest } = props;

  return rest as StrippedProps<T>;
}

/**
 * A registry used to map out components by fieldType property defined in the config.
 * @param field
 * @param error
 * @param errorMessage
 * @param step
 */
const Registry: FC<RegistryProps> = ({ field, error, errorMessage, step }) => {
 
  const { counterSteps, config } = useFlowContext();
  const { setFormData, formData, setErrors, setMetadata } = useRegistrationStore();
  const { handleBlur } = useFieldValidator(config);

  // Create a unique container ID for the step
  const stepContainerId = useMemo(() => `step-container-${step.name}`, [step.name]);
  const { handleEnterKey } = useEnterKeyNavigation(stepContainerId);

  const groupedFields = useFieldRenderer((field as TCardField)?.fields || [], step);

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setErrors({ [field.name]: '' });
    setFormData({ [field.name]: e.currentTarget.value });
  };

  const handleSelect = (checked: string) => {
    setFormData({ [field.name]: checked });
  };

  const handleFocus = () => {
    if (GTM_FOCUS_FIELDS[field.name as keyof typeof GTM_FOCUS_FIELDS]) {
      setMetadata({ [`${field.name}HadFocus`]: true });
    }
  }

  const fieldMap: {
    [K in FieldType]: <T extends TField & { fieldType: K }>(field: T) => ReactNode
  } = {
    /**
     * Primitives
     * These components directly render data from a config.
     * They are simple and straightforward, often used as children within more complex components.
     */
    [FieldType.Input]: (field) => (
      <Input
        {...stripFieldProps(field)}
        id={field.name}
        onChange={handleChange}
        value={formData?.[field.name] || ''}
        errorMessage={errorMessage}
        error={error ? true : undefined}
        onClear={() => setFormData({ [field.name]: '' })}
        onBlur={() => handleBlur(field)}
        onFocus={() => handleFocus()}
        enterKeyHint="next"
        onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => handleEnterKey(e)}
        data-testid={TEST_ID.INPUT + field.name}
        data-error={error ? true : undefined}
        wrapperClass="hide-close-button-on-blur"
        maxLength={NAME_FIELDS.includes(field.name) ? NAME_FIELD_MAX_LENGTH : undefined}
      />
    ),
    [FieldType.Card]: (field) => (
      <Card
        title={field.label}
        description={field.description}
        navigationText={
          step.navigationText
            ? step.navigationText
            : counterSteps!.indexOf(step.name) >= 0
              ? interpolate(step.currentStepText, {
                currentStep: counterSteps!.indexOf(step.name)! + 1,
                totalSteps:  counterSteps?.length ?? '',
              })
              : ''
        }
        className="bg-white flex flex-col"
      >
        <div className="flex flex-col gap-sm">
          {groupedFields}
        </div>
      </Card>
    ),
    [FieldType.Callout]: (field) => (
      <Callout
        {...field}
        alert={field.alert || 'info'}
      >
        <div className="flex-wrap">
          <span className="font-bold">{field.label}</span>
          <span>&nbsp;{field.description}</span>
        </div>
      </Callout>
    ),
    [FieldType.Selector]: (field) => (
      <Selector name={field.name}>
        {field.options?.map((option) =>
          <Selector.Radio
            key={option.value}
            value={formData?.[field.name]}
            name={field.name}
            onChange={() => setFormData({ [field.name]: option.value })}
            className="text-center has-[:checked]:bg-purple-0 has-[:checked]:text-purple-80"
            checked={formData?.[field.name] === option.value}
            data-testid={TEST_ID.SELECT + field.name + '-' + option.value}
          >
            {option.label}
          </Selector.Radio>,
        )}
      </Selector>
    ),
    [FieldType.Text]: (field) => (
      <Text {...field} />
    ),
    /** Complex Components
     * Include components that may involve additional logic specific to different markets or contexts.
     * These components encapsulate more intricate behavior and may integrate with other parts of the application.
     */
    [FieldType.PhoneNumberInput]: (field) => (
      <PhoneNumberInput
        {...field}
        onChange={handleChange}
        value={formData?.[field.name] || ''}
        onClear={() => setFormData({ [field.name]: '' })}
        onBlur={() => handleBlur(field)}
        onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => handleEnterKey(e)}
      />
    ),
    [FieldType.MultiMarketingConsent]: (field) => (
      <MultiMarketingConsent
        {...field}
        onChange={handleChange}
        value={formData?.[field.name] || ''}
        onClear={() => setFormData({ [field.name]: '' })}
        onBlur={() => handleBlur(field)}
      />
    ),
    [FieldType.CountrySelector]: (field) => (
      <CountrySelector
        {...field}
        onChange={handleSelect}
        onBlur={() => handleBlur(field)}
      />
    ),
    [FieldType.ProvinceSelector]: (field) => (
      <ProvinceSelector
        {...field}
        onChange={handleSelect}
        onBlur={() => handleBlur(field)}
      />
    ),
    [FieldType.SelectorDrawer]: (field) => (
      <SelectorDrawer
        {...field}
        onChange={handleSelect}
        onBlur={() => handleBlur(field)}
      />
    ),
    [FieldType.EnterAddressManuallyText]: (field) => (
      <EnterAddressManuallyText {...field} />
    ),
    [FieldType.EmailInput]: (field) => (
      <EmailInput
        {...field}
        validation={field.validation}
        value={formData?.[field.name]}
        onBlur={() => handleBlur(field)}
        onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => handleEnterKey(e)}
      />
    ),
    [FieldType.WelcomeOffer]: (field) => (
      <WelcomeOffer {...field} />
    ),
    [FieldType.AutoCompleteAddressInput]: (field) => (
      <AutoCompleteAddressInput
        {...field}
      />
    ),
    [FieldType.DateInput]: (field) => (
      <DateInput
        {...field}
        onBlur={() => handleBlur(field)}
        onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => handleEnterKey(e)}
      />
    ),
    [FieldType.ConsentField]: (field) => (
      <ConsentField {...field}
        errorMessage={errorMessage}
        error={error ? true : undefined}
        data-error={error ? true : undefined}
      />
    ),
    [FieldType.WoCallout]: (field) => (
      <WoCallout {...field} />
    ),
    [FieldType.DepositIncrementer]: (field) => (
      <DepositIncrementer {...field}
      />
    ),
    [FieldType.DepositTypeSelector]: (field) => (
      <DepositTypeSelector {...field} />
    ),
    [FieldType.DepositLimits]: (field) => (
      <DepositLimitsWrapper {...field}
        onBlur={(field) => handleBlur(field as TInputField)}
      />
    ),
    [FieldType.PostcodeInput]: (field) => (
      <PostcodeInput
        {...field}
        onBlur={() => handleBlur(field)}
        onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => handleEnterKey(e)}
      />
    ),
    [FieldType.IdentificationNumberES]: (field) => (
      <IdentificationNumberES
        {...field}
      />
    ),
    [FieldType.IdentityProtectedPassword]: (field) => (
      <IdentityProtectedPassword
        {...field}
        onBlur={() => handleBlur(field)}
        onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => handleEnterKey(e)}
      />
    ),
    [FieldType.PasswordInput]: (field) => (
      <PasswordInput
        {...field}
        onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => handleEnterKey(e)}
      />
    ),
    [FieldType.EsPasswordInput]: (field) => (
      <EsPasswordInput
        {...field}
        onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => handleEnterKey(e)}
      />
    ),
  };

  function renderField<K extends FieldType>(
    field: Extract<TField, { fieldType: K }>,
  ): ReactNode {
    if (
      !fieldMap[field.fieldType]
      && (REACT_APP_ENVIRONMENT === ENVIRONMENTS.DEV || REACT_APP_ENVIRONMENT === ENVIRONMENTS.TEST)
    ) {
      console.info('Field type not found:', { fieldType: field.fieldType });

      return <div className="text-red-50 font-semibold text-lg">🚧 Under Construction 🚧</div>
    }

    return fieldMap[field.fieldType](field);
  }

  return renderField(field as TField) || null;
};

const Memoized = memo(Registry);
Memoized.displayName = 'Registry';
export default Memoized;
